import { useCallback } from 'react';
import { useRelayEnvironment } from 'react-relay';
import { fetchQuery, graphql } from 'relay-runtime';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { MAX_AMOUNT_OF_COLUMNS } from '../../common/constants.tsx';
import { useIssueNavigatorFeatures } from '../../controllers/features-context/index.tsx';
import type { refetchIssue_IssueNavigatorQuery } from './__generated__/refetchIssue_IssueNavigatorQuery.graphql';

type OnIssueByFieldsRefetchType = (issuekey: string) => void;

export const useIssueByFieldsRefetch = (fieldSets: string[]): OnIssueByFieldsRefetchType => {
	const { cloudId } = useTenantContext();
	const isConditionalFormattingEnabled =
		fg('jira_list_conditional_formatting') &&
		// eslint-disable-next-line  react-hooks/rules-of-hooks
		useIssueNavigatorFeatures().isConditionalFormattingEnabled;
	const environment = useRelayEnvironment();

	return useCallback(
		(issuekey: string) =>
			fetchQuery<refetchIssue_IssueNavigatorQuery>(
				environment,
				/* eslint-disable @atlassian/relay/unused-fields, @atlassian/relay/must-colocate-fragment-spreads */
				// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
				graphql`
					query refetchIssue_IssueNavigatorQuery(
						$cloudId: ID!
						$issueSearchInput: JiraIssueSearchInput!
						$fieldSetIds: [String!]!
						$amountOfColumns: Int!
						$isConditionalFormattingEnabled: Boolean!
						$isTimelineEnabled: Boolean!
					) {
						jira {
							issueSearchStable(cloudId: $cloudId, issueSearchInput: $issueSearchInput, first: 1) {
								edges {
									node {
										fieldSetsById(fieldSetIds: $fieldSetIds, first: $amountOfColumns) {
											...IssueRow_fieldSets
												@arguments(
													isInlineEditingEnabled: true
													isDensityFull: false
													isConditionalFormattingEnabled: $isConditionalFormattingEnabled
													isTimelineEnabled: $isTimelineEnabled
													isLinkRelationshipFieldEnabled: false
													isEditableSyncLoaded: false
												)
											...main_issueNavigator_Card_fieldSets
										}
									}
								}
							}
						}
					}
				`,
				/* eslint-enable @atlassian/relay/unused-fields, @atlassian/relay/must-colocate-fragment-spreads */
				{
					cloudId,
					issueSearchInput: { jql: `issueKey=${issuekey}` },
					fieldSetIds: fieldSets,
					amountOfColumns: MAX_AMOUNT_OF_COLUMNS,
					isConditionalFormattingEnabled,
					isTimelineEnabled: false,
				},
				{ fetchPolicy: 'network-only' },
			).toPromise(),
		[cloudId, environment, fieldSets, isConditionalFormattingEnabled],
	);
};
